import { lazy, Suspense, useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { useApp, request, type View } from './app/store';
import { buildAnalysisJson, buildYearCsv, download, importAnalysis, stamp } from './app/exports';
import { go, useDismiss, useRoute } from './ui/hooks';
import {
  IconChevron,
  IconCompare,
  IconDetail,
  IconDownload,
  IconOverview,
  IconPortfolio,
  IconPrint,
  IconReport,
  IconSliders,
  IconTrash,
  IconUpload,
  IconArrowRight,
  IconFuture,
} from './ui/icons';
import { Notices, QueueBar } from './ui/components/Panels';
import { PortfolioView } from './ui/views/PortfolioView';
import { OverviewView } from './ui/views/OverviewView';

// Seltener genutzte Ansichten erst bei Bedarf laden
const DetailView = lazy(() => import('./ui/views/DetailView').then((m) => ({ default: m.DetailView })));
const CompareView = lazy(() => import('./ui/views/CompareView').then((m) => ({ default: m.CompareView })));
const AssumptionsView = lazy(() => import('./ui/views/AssumptionsView').then((m) => ({ default: m.AssumptionsView })));
const ReportView = lazy(() => import('./ui/views/ReportView').then((m) => ({ default: m.ReportView })));
const FutureView = lazy(() => import('./ui/views/FutureView').then((m) => ({ default: m.FutureView })));

const NAV: { view: View; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
  { view: 'portfolio', label: 'Portfolio', icon: IconPortfolio },
  { view: 'uebersicht', label: 'Übersicht', icon: IconOverview },
  { view: 'detail', label: 'Detail', icon: IconDetail },
  { view: 'zukunft', label: 'Zukunft', icon: IconFuture },
  { view: 'vergleich', label: 'Vergleich', icon: IconCompare },
  { view: 'annahmen', label: 'Annahmen', icon: IconSliders },
  { view: 'bericht', label: 'Bericht', icon: IconReport },
];

const TITLES: Record<View, string> = {
  portfolio: 'Portfolio',
  uebersicht: 'Standortübersicht',
  detail: 'Klima im Detail',
  zukunft: 'Zukunftsklima',
  vergleich: 'Standortvergleich',
  annahmen: 'Annahmen und Methodik',
  bericht: 'Bericht',
};

function NavLinks({ current }: { current: View }) {
  return (
    <>
      {NAV.map(({ view, label, icon: Icon }) => (
        <a key={view} href={`#/${view}`} className="nav-item" aria-current={current === view ? 'page' : undefined}>
          <Icon />
          <span>{label}</span>
        </a>
      ))}
    </>
  );
}

function BuildingPicker() {
  const buildings = useApp((s) => s.buildings);
  const focusId = useApp((s) => s.focusId);
  const setFocus = useApp((s) => s.setFocus);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState('');
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), btn, pop);
  const cur = buildings.find((b) => b.id === focusId);
  const list = buildings.filter((b) => !f || b.label.toLowerCase().includes(f.toLowerCase())).slice(0, 200);
  return (
    <div className="picker" style={{ position: 'relative', minWidth: 0 }}>
      <button ref={btn} type="button" className="btn" style={{ maxWidth: 340, minWidth: 0, width: '100%' }} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur ? cur.label : 'Kein Gebäude'}</span>
        <IconChevron />
      </button>
      {open && (
        <div ref={pop} className="popover" style={{ top: 'calc(100% + 6px)', left: 0, width: 340, maxWidth: 'calc(100vw - 32px)' }}>
          {buildings.length > 8 && <input className="input" placeholder="Gebäude filtern" value={f} onChange={(e) => setF(e.target.value)} autoFocus aria-label="Gebäude filtern" style={{ marginBottom: 6 }} />}
          <div role="listbox" aria-label="Gebäude wählen" style={{ maxHeight: 360, overflowY: 'auto' }}>
            {list.map((b) => (
              <button
                key={b.id}
                type="button"
                role="option"
                aria-selected={b.id === focusId}
                className="menu-item"
                style={b.id === focusId ? { background: 'var(--primary-soft)' } : undefined}
                onClick={() => {
                  setFocus(b.id);
                  setOpen(false);
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.label}</span>
              </button>
            ))}
          </div>
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 6 }}>
            <button
              type="button"
              className="menu-item"
              onClick={() => {
                setOpen(false);
                go('portfolio');
              }}
            >
              <IconPortfolio /> Gebäude hinzufügen oder verwalten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ModeSwitch() {
  const mode = useApp((s) => s.mode);
  const setMode = useApp((s) => s.setMode);
  const keyed = useApp((s) => s.keyed);
  return (
    <div className="row" style={{ gap: 8 }}>
      <span className={`chip chip-dot ${mode === 'demo' ? 'chip-demo' : 'chip-live'}`} title={mode === 'demo' ? 'Offline-Demomodus: Datensnapshot, kein Netzabruf' : keyed ? 'Live-Daten mit Schlüssel' : 'Live-Daten, freie API'}>
        {mode === 'demo' ? 'Offline-Demo · Snapshot' : 'Live · Open-Meteo'}
      </span>
      <div className="seg hide-mobile" role="group" aria-label="Datenquelle">
        <button type="button" aria-pressed={mode === 'live'} onClick={() => void setMode('live')}>
          Live
        </button>
        <button type="button" aria-pressed={mode === 'demo'} onClick={() => void setMode('demo')}>
          Demo
        </button>
      </div>
    </div>
  );
}

function ExportMenu() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const notify = useApp((s) => s.notify);
  const mode = useApp((s) => s.mode);
  useDismiss(open, () => setOpen(false), btn, pop);

  const run = async (fn: () => Promise<void> | void) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      notify('danger', 'Aktion fehlgeschlagen', (e as Error).message);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button ref={btn} type="button" className="btn" aria-haspopup="menu" aria-expanded={open} aria-label="Export und Import" onClick={() => setOpen((o) => !o)} disabled={busy}>
        <IconDownload />
        <span className="hide-mobile">Export</span>
      </button>
      {open && (
        <div ref={pop} className="popover" role="menu" style={{ top: 'calc(100% + 6px)', right: 0, width: 320 }}>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() =>
              void run(() => {
                download(`klimaraum_jahreskennzahlen_${stamp()}.csv`, buildYearCsv(), 'text/csv;charset=utf-8');
                notify('info', 'CSV exportiert', 'UTF-8, Semikolon, Dezimalpunkt, eine Zeile je Gebäude und Jahr.');
              })
            }
          >
            <IconDownload /> CSV: Jahreskennzahlen
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() =>
              void run(async () => {
                const j = await buildAnalysisJson(true);
                download(`klimaraum_analyse_${stamp()}.json`, JSON.stringify(j), 'application/json');
                notify('info', 'Analyse exportiert', `${j.state.buildings.length} Gebäude, ${j.inputData?.length ?? 0} Rasterzellen mit Eingabedaten.`);
              })
            }
          >
            <IconDownload /> JSON: Analyse mit Eingabedaten
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() =>
              void run(async () => {
                const j = await buildAnalysisJson(false);
                download(`klimaraum_analyse_ohne_daten_${stamp()}.json`, JSON.stringify(j, null, 1), 'application/json');
              })
            }
          >
            <IconDownload /> JSON: Analyse ohne Eingabedaten
          </button>
          <button type="button" role="menuitem" className="menu-item" onClick={() => void run(() => file.current?.click())}>
            <IconUpload /> JSON-Analyse einlesen
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => {
              setOpen(false);
              go('bericht');
            }}
          >
            <IconPrint /> Bericht drucken / PDF
          </button>
          {mode === 'live' && (
            <button
              type="button"
              role="menuitem"
              className="menu-item"
              onClick={() =>
                void run(async () => {
                  if (!confirm('Alle im Browser zwischengespeicherten Klimadaten löschen? Sie werden bei Bedarf erneut abgerufen.')) return;
                  await request({ kind: 'clearCache' });
                  notify('info', 'Browser-Cache geleert');
                })
              }
            >
              <IconTrash /> Browser-Cache leeren
            </button>
          )}
        </div>
      )}
      <input
        ref={file}
        type="file"
        accept="application/json,.json"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          try {
            const out = await importAnalysis(await f.text());
            notify('info', 'Analyse eingelesen', `${out.buildings} Gebäude${out.withData ? ', Eingabedaten übernommen' : ', Daten werden neu abgerufen'}. Reproduktion wird geprüft …`);
            const c = await out.check;
            if (c.identical) notify('info', 'Reproduktion bestätigt', `${c.compared} Kennzahlwerte identisch mit dem Export.`);
            else notify('warn', 'Reproduktion nicht vollständig', `${c.missing} Werte fehlen noch, grösste Abweichung ${c.maxDiff}.`);
          } catch (err) {
            notify('danger', 'Import fehlgeschlagen', (err as Error).message);
          }
        }}
      />
    </div>
  );
}

const FOOTER_LINKS: { href: string; label: string }[] = [
  { href: '#/annahmen', label: 'Methodik und Annahmen' },
  { href: 'https://open-meteo.com/en/docs/historical-weather-api', label: 'Open-Meteo Historical Weather API' },
  { href: 'https://cds.climate.copernicus.eu/datasets/reanalysis-era5-land', label: 'ERA5-Land (Copernicus)' },
  { href: 'https://api3.geo.admin.ch/', label: 'geo.admin.ch (swisstopo, BFS)' },
  { href: 'https://www.meteoschweiz.admin.ch/klima/klimawandel/klimaszenarien.html', label: 'Klima CH2025 (Klimaszenarien)' },
  { href: 'https://climateknowledgeportal.worldbank.org/', label: 'Weltbank Climate Change Knowledge Portal' },
];

function Breadcrumb({ view }: { view: View }) {
  const focus = useApp((s) => s.buildings.find((b) => b.id === s.focusId));
  const items: { label: string; href?: string }[] = [{ label: 'Portfolio', href: '#/portfolio' }];
  if (view === 'uebersicht' || view === 'detail' || view === 'zukunft' || view === 'bericht') {
    if (focus) items.push({ label: focus.label, href: '#/uebersicht' });
    if (view !== 'uebersicht') items.push({ label: TITLES[view] });
  } else if (view === 'vergleich') items.push({ label: TITLES[view] });
  else if (view === 'annahmen') items.splice(0, 1, { label: TITLES[view] });
  const last = items.length - 1;
  return (
    <nav className="breadcrumb wrap" aria-label="Pfad">
      <ol>
        {items.map((it, i) => (
          <li key={`${it.label}-${i}`}>{it.href && i < last ? <a href={it.href}>{it.label}</a> : <span aria-current={i === last ? 'page' : undefined}>{it.label}</span>}</li>
        ))}
      </ol>
    </nav>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer no-print">
      <div className="wrap">
        <div>
          <h2>Über diese Anwendung</h2>
          <p>
            KLIMA / RAUM wertet historische Reanalysedaten für Gebäudestandorte aus: Gradtage, Hitze, warme Nächte und das theoretische Nachtauskühlpotenzial im Vergleich zum Mittel 1991–2020, ergänzt um Änderungen aus Klimaszenarien. Unabhängige Fachanwendung, kein Angebot von MeteoSchweiz oder einer anderen Bundesstelle.
          </p>
        </div>
        <div>
          <h2>Grenzen</h2>
          <p>Aussenluftdaten sind keine Raumtemperaturen, Gradtage keine Energieverbräuche, rechnerisches Kühlpotenzial ist keine Betriebsfreigabe. Rasterdaten bilden städtische Mikroklimata nur begrenzt ab.</p>
        </div>
        <div>
          <h2>Weitere Informationen</h2>
          <ul className="footer-links">
            {FOOTER_LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} {...(l.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>
                  {l.label}
                  <IconArrowRight />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="wrap">Wetterdaten: Open-Meteo.com, CC BY 4.0, enthält modifizierte Copernicus Climate Change Service Informationen (ERA5-Land, ERA5). Zukunftsklima: Klima CH2025, MeteoSchweiz und ETH Zürich, CC BY 4.0; Weltbank CCKP, ODbL, CMIP6 CC BY-SA 4.0. Adressen, GWR und Höhen: geo.admin.ch. Version 1.1.</div>
      </div>
    </footer>
  );
}

export function App() {
  const { view } = useRoute();
  const boot = useApp((s) => s.boot);
  const booted = useApp((s) => s.booted);
  const fatal = useApp((s) => s.fatal);
  const saveError = useApp((s) => s.saveError);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    void boot();
  }, [boot]);

  useEffect(() => {
    document.title = `${TITLES[view]} · KLIMA / RAUM`;
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [view]);

  return (
    <div className="app">
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>
      <div className="metabar">
        <div className="wrap">
          <span className="hide-mobile">Klima- und Gebäudeanalyse</span>
          <div className="spacer" />
          <QueueBar />
          <ModeSwitch />
        </div>
      </div>
      <header className="site-header">
        <div className="wrap">
          <a href="#/uebersicht" className="brand" aria-label="KLIMA / RAUM, Standortübersicht">
            <span className="brand-mark">
              KLIMA <span className="slash">/</span> RAUM
            </span>
            <span className="brand-name">
              Klima- und Gebäudestrategien
              <small>Historische Klimaanalyse für Standorte und Portfolios</small>
            </span>
          </a>
          <div className="spacer" />
          <BuildingPicker />
          <ExportMenu />
        </div>
      </header>
      <nav className="main-nav" aria-label="Hauptnavigation">
        <div className="wrap">
          <NavLinks current={view} />
        </div>
      </nav>
      <Breadcrumb view={view} />
      <main id="inhalt" className="content wrap" ref={mainRef} tabIndex={-1} style={{ outline: 'none' }}>
        {fatal && (
          <div className="notice notice-danger" role="alert" style={{ marginBottom: 16 }}>
            <div className="notice-body">
              <div className="notice-title">Rechenkern gestört</div>
              <div className="small">{fatal}. Seite neu laden; gespeicherte Analysen bleiben erhalten.</div>
            </div>
          </div>
        )}
        {saveError && (
          <div className="notice notice-warn" role="status" style={{ marginBottom: 16 }}>
            <div className="notice-body">Lokales Speichern nicht möglich (Speicher voll oder gesperrt). Analyse als JSON exportieren, um sie zu sichern.</div>
          </div>
        )}
        <Suspense fallback={<div className="skeleton" style={{ height: 240 }} />}>
        {!booted ? (
          <div className="skeleton" style={{ height: 240 }} />
        ) : view === 'portfolio' ? (
          <PortfolioView />
        ) : view === 'detail' ? (
          <DetailView />
        ) : view === 'zukunft' ? (
          <FutureView />
        ) : view === 'vergleich' ? (
          <CompareView />
        ) : view === 'annahmen' ? (
          <AssumptionsView />
        ) : view === 'bericht' ? (
          <ReportView />
        ) : (
          <OverviewView />
        )}
        </Suspense>
      </main>
      <SiteFooter />
      <div className="notices-wrap">
        <Notices />
      </div>
    </div>
  );
}
